import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter, createBrowserRouter, RouterProvider } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { CssBaseline, ThemeProvider, StyledEngineProvider } from '@mui/material';
import { Toaster } from 'sonner';
import { auth0Config } from './services/auth0';
import PublicRoutes from './PublicRoutes';
import configureStore from './store/configureStore';
import theme from './styles/themes/theme';
import { setAuth0Functions } from './api/configureAuth';
import { setAuth0Functions as setStoreAuth0Functions } from './store/fetchBaseQuery';
import { ApplicationLoading } from './common';
import { publicPaths } from './util/constants';
import { MainContentProvider } from './contexts/MainContentContext';
import routes from './Routes';
import { runtimeConfig } from './config/runtimeConfig';

const store = configureStore();
const router = createBrowserRouter(routes);

// Separate component to handle authenticated state
const AuthenticatedApp: React.FC<{ LDProvider: React.ComponentType<any> }> = ({ LDProvider }) => {
  const { isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  React.useEffect(() => {
    if (isAuthenticated && getAccessTokenSilently) {
      setAuth0Functions(getAccessTokenSilently);
      setStoreAuth0Functions(getAccessTokenSilently);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Redirect to login if not authenticated and not loading
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
        authorizationParams: {
          audience: auth0Config.authorizationParams.audience,
          scope: auth0Config.authorizationParams.scope,
          connection: auth0Config.authorizationParams.connection,
        },
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  if (isLoading || !isAuthenticated) {
    return <ApplicationLoading />;
  }

  return (
    <LDProvider>
      <Provider store={store}>
        <CookiesProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StyledEngineProvider injectFirst>
              <MainContentProvider>
                <RouterProvider router={router} future={{ v7_startTransition: true }} />
              </MainContentProvider>
            </StyledEngineProvider>
          </LocalizationProvider>
        </CookiesProvider>
      </Provider>
    </LDProvider>
  );
};

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: runtimeConfig.ldClientSideId || '',
    user: {
      key: localStorage.getItem('externalId') || 'anonymous',
      email: localStorage.getItem('emailAddress') || undefined,
      anonymous: !localStorage.getItem('externalId'),
    },
  });

  const onRedirectCallback = (appState?: any) => {
    const targetUrl = appState?.returnTo || window.location.pathname + window.location.search;
    window.history.replaceState({}, document.title, targetUrl);
  };

  const isPublicRoute = publicPaths.includes(window.location.pathname);
  const root = createRoot(document.getElementById('root')!);

  if (isPublicRoute) {
    root.render(
      <ThemeProvider theme={theme}>
        <StyledEngineProvider injectFirst>
          <CssBaseline />
          <BrowserRouter>
            <PublicRoutes />
          </BrowserRouter>
          <Toaster position="bottom-center" visibleToasts={5} />
        </StyledEngineProvider>
      </ThemeProvider>,
    );
  } else {
    root.render(
      <ThemeProvider theme={theme}>
        <Auth0Provider {...auth0Config} onRedirectCallback={onRedirectCallback}>
          <AuthenticatedApp LDProvider={LDProvider} />
        </Auth0Provider>
        <Toaster position="bottom-center" visibleToasts={5} />
      </ThemeProvider>,
    );
  }
})();
